import {Dayjs} from "dayjs";
import {IProtocolStep, TProtocolStepId} from "@/protocols/types/protocol.ts";

/**
 * Represents a timing warning for a step.
 */
export interface TimingWarning {
    stepId: TProtocolStepId;
    reason: string;
    severity: "warning";
}

/**
 * Checks for timing inconsistencies between steps in the natural protocol order.
 * 
 * A warning is generated when:
 * - A step's startTime precedes the startTime of the previous step in natural order
 *   (when natural order says it should come after, but timestamp says it came before)
 * - A step has invalid timing (startTime > endTime)
 * 
 * @param orderedSteps - Steps in natural protocol order (from useProtocolStepOrdering)
 * @returns Map of stepId -> TimingWarning for steps with timing issues
 */
export function checkStepTimingWarnings(
    orderedSteps: IProtocolStep[]
): Map<TProtocolStepId, TimingWarning> {
    const timingWarnings = new Map<TProtocolStepId, TimingWarning>();

    for (let i = 1; i < orderedSteps.length; i++) {
        const currentStep = orderedSteps[i];
        const previousStep = orderedSteps[i - 1];

        const currentStartTime = currentStep.startTime;
        const previousStartTime = previousStep.startTime;

        // Check if current step's startTime precedes previous step's startTime
        // when natural order says current should come after previous
        if (currentStartTime && previousStartTime) {
            if (currentStartTime.isBefore(previousStartTime)) {
                timingWarnings.set(currentStep.id, {
                    stepId: currentStep.id,
                    reason: `startTime (${currentStartTime.format("HH:mm:ss")}) precedes previous step's startTime (${previousStartTime.format("HH:mm:ss")})`,
                    severity: "warning"
                });
            }
        }

        // Check for invalid timing: startTime > endTime
        if (currentStartTime && currentStep.endTime) {
            if (currentStartTime.isAfter(currentStep.endTime)) {
                timingWarnings.set(currentStep.id, {
                    stepId: currentStep.id,
                    reason: `startTime (${currentStartTime.format("HH:mm:ss")}) is after endTime (${currentStep.endTime.format("HH:mm:ss")})`,
                    severity: "warning"
                });
            }
        }
    }

    // Check first step for invalid timing as well
    if (orderedSteps.length > 0) {
        const firstStep = orderedSteps[0];
        if (firstStep.startTime && firstStep.endTime) {
            if (firstStep.startTime.isAfter(firstStep.endTime)) {
                timingWarnings.set(firstStep.id, {
                    stepId: firstStep.id,
                    reason: `startTime (${firstStep.startTime.format("HH:mm:ss")}) is after endTime (${firstStep.endTime.format("HH:mm:ss")})`,
                    severity: "warning"
                });
            }
        }
    }

    return timingWarnings;
}

/**
 * Checks if an event's timestamp falls outside its parent step's time boundary.
 * 
 * @param eventStartTime - Event's start time
 * @param stepStartTime - Parent step's start time (may be undefined)
 * @param stepEndTime - Parent step's end time (may be undefined)
 * @returns true if event falls outside step boundaries
 */
export function isEventOutsideStepBoundary(
    eventStartTime: Dayjs,
    stepStartTime: Dayjs | undefined,
    stepEndTime: Dayjs | undefined
): boolean {
    if (!stepStartTime) {
        // Step hasn't started yet, any event timestamp is outside
        return true;
    }

    if (stepEndTime) {
        // Event must be within [stepStartTime, stepEndTime]
        return eventStartTime.isBefore(stepStartTime) || eventStartTime.isAfter(stepEndTime);
    }

    // No endTime - event is outside if before step start
    return eventStartTime.isBefore(stepStartTime);
}